import { useEffect, useEffectEvent, useRef } from "react";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type {
  ChangedResourceType,
  ResourceChangeProgressNotification,
  ResourceChangedNotification,
} from "../api/types/resourceChangesTypes";
import ResourceChangesContext from "../context/ResourceChangesContext";
import type { ReloadQueue } from "./resourceChangeHandling";
import { createReloadQueue, getResourceChangeAction } from "./resourceChangeHandling";

interface Options {
  onChanged: () => Promise<void>;
  onProgress?: (progress: ResourceChangeProgressNotification) => void;
  // Whether the loaded resource shows a running operation; Progress reaches `onProgress` only then.
  isOperationRunning?: boolean;
  // Holds reloads while true and runs a held one when it turns false.
  paused?: boolean;
}

/** Does nothing while the live-updates connection is down; there is no polling fallback. */
const useResourceChanged = (
  resourceType: ChangedResourceType,
  resourceId: string,
  { onChanged, onProgress, isOperationRunning, paused }: Options
) => {
  const { addListener } = useDefinedContext(ResourceChangesContext, "ResourceChangesContext");

  const reloadQueueRef = useRef<ReloadQueue>(undefined);

  const reload = useEffectEvent(onChanged);
  const isPaused = useEffectEvent(() => paused ?? false);

  const handleChange = useEffectEvent((notification: ResourceChangedNotification) => {
    const action = getResourceChangeAction(notification, {
      handlesProgress: onProgress !== undefined,
      isOperationRunning: isOperationRunning ?? false,
    });

    if (action.type === "reload") {
      reloadQueueRef.current?.request();
    } else if (action.type === "showProgress") {
      onProgress?.(action.progress);
    }
  });

  useEffect(() => {
    const reloadQueue = createReloadQueue(() => reload());
    reloadQueue.setPaused(isPaused());
    reloadQueueRef.current = reloadQueue;

    const removeListener = addListener(resourceType, resourceId, {
      onChange: (notification) => handleChange(notification),
      onSubscribed: () => reloadQueue.request(),
    });

    return () => {
      removeListener();
      reloadQueue.dispose();
      reloadQueueRef.current = undefined;
    };
  }, [addListener, resourceType, resourceId]);

  useEffect(() => {
    reloadQueueRef.current?.setPaused(paused ?? false);
  }, [paused]);
};

export default useResourceChanged;
